import { Mutation, Query } from '@varo/graphql-schema-types';
import { Href, Redirect, router, Stack, usePathname } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { identityIdFromToken } from 'services/analytics/helpers/identityIdFromToken';
import { getAndClearPostLoginRoute } from 'services/deeplinks/deeplinkManager';
import { syncPushTokenWithBackend } from 'services/engagement/engagementManager';
import { UpdatePushToken } from 'services/graphql/mutations/UpdatePushToken';
import GetProfileInfo from 'services/graphql/queries/GetProfileInfo';
import { syncUserIdentityToServices } from 'services/userInfo/userInfoManager';
import ScreenAnalytics from 'shared/components/ScreenAnalytics/ScreenAnalytics';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import {
  AbortError,
  useCancellableMutation,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useRedirectClosedAccount from 'shared/hooks/useRedirectClosedAccount/useRedirectClosedAccount';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { useAuth } from 'shared/providers/auth';
import theme from 'shared/styles/theme';
import { useIdempotencyToken } from 'shared/utils/idempotencyTokens/idempotencyTokenUtils';
import { trackGeolocationStatus } from 'shared/utils/location/trackGeolocationStatus';
import { logBreadcrumb, logError } from 'shared/utils/sentry/sentry';
import { useQuery } from 'urql';

function AuthenticatedLayout() {
  const { isLoggedIn, loginEvaluated, idToken } = useAuth();
  const [hasSyncedIdentity, setHasSyncedIdentity] = useState(false);
  const isSyncingIdentity = useRef(false);
  const pathname = usePathname();
  const { idempotencyToken } = useIdempotencyToken('0');
  const [, updatePushTokenMutation] =
    useCancellableMutation<Mutation>(UpdatePushToken);

  // Signup routes don't have customerInfo yet - skip identity sync blocking
  const isSignupRoute = pathname.includes('/signup');

  const [result, reexecuteQuery] = useQuery<Query>({
    query: GetProfileInfo,
    pause: !idToken || isSignupRoute || hasSyncedIdentity,
  });

  // Restricts customers with a closed/deceased account to /reactivation and
  // the /signup reapplication flow, redirecting away from any other page.
  useRedirectClosedAccount();

  // Sync user identity to external services when user profile is fetched
  useEffect(() => {
    const syncUserIdentity = async () => {
      // Prevent re-entry while sync is in progress or already complete
      if (hasSyncedIdentity || isSyncingIdentity.current) {
        return;
      }
      isSyncingIdentity.current = true;

      if (!idToken || !result.data?.user?.customerInfo) {
        isSyncingIdentity.current = false;
        return;
      }

      try {
        const identityId = identityIdFromToken(idToken);
        await syncUserIdentityToServices(
          result.data.user.customerInfo,
          identityId,
        );

        // Sync push token with backend (Varo's backend, not Braze)
        await syncPushTokenWithBackend(async (variables) => {
          const mutationResult = await updatePushTokenMutation(variables);
          return { data: mutationResult.data ?? undefined };
        }, idempotencyToken);

        // Track geolocation permission status for analytics
        void trackGeolocationStatus();

        setHasSyncedIdentity(true);
      } catch (error) {
        // Don't update state if the mutation was cancelled (component unmounted)
        if (error instanceof AbortError) {
          return;
        }
        logError('Failed to sync user identity to services');
        // Allow rendering even if sync fails - login should succeed
        setHasSyncedIdentity(true);
      }
    };

    void syncUserIdentity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, result.data, idempotencyToken, updatePushTokenMutation]);

  // Reset sync flag when user logs out
  useEffect(() => {
    if (!isLoggedIn) {
      setHasSyncedIdentity(false);
      isSyncingIdentity.current = false;
    }
  }, [isLoggedIn]);

  // Process pending deeplink after login sync completes
  useEffect(() => {
    const processPendingDeeplink = async () => {
      if (!hasSyncedIdentity) {
        return;
      }

      const deeplink = await getAndClearPostLoginRoute();
      if (deeplink) {
        logBreadcrumb({
          level: 'info',
          message: 'Processing post-login deeplink',
          data: { route: deeplink.route, params: deeplink.params },
        });
        router.navigate({
          pathname: deeplink.route,
          params: deeplink.params,
        } as Href);
      }
    };

    void processPendingDeeplink();
  }, [hasSyncedIdentity]);

  // If not logged in, redirect to login
  if (loginEvaluated && !isLoggedIn) {
    return <Redirect href="/login" />;
  }

  // Handle query errors
  if (result.error && !isSignupRoute && isLoggedIn) {
    return (
      <ErrorScreen
        screenType={ErrorScreenType.Error}
        messageHeadline="Unable to load your account"
        messageBody="We're having trouble loading your information. Please try again."
        primaryButton={{
          title: 'Try Again',
          onPress: () => {
            reexecuteQuery({ requestPolicy: 'network-only' });
          },
          isLoading: result.fetching,
        }}
        error={result.error}
      />
    );
  }

  const isIdentitySyncing = !isSignupRoute && !hasSyncedIdentity;

  return (
    <>
      <ScreenAnalytics />
      <Stack
        screenOptions={{
          contentStyle: {
            backgroundColor: theme.color.background.primary,
          },
          headerShown: false,
        }}
      />
      <ScreenTransitionOverlay
        isLoading={isIdentitySyncing}
        LoadingComponent={<FullScreenLoading />}
        ErrorComponent={null}
      />
    </>
  );
}

export default AuthenticatedLayout;
